import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { ChatEmbedded } from '@microsoft/sharepointembedded-copilotchat-react';
import { copilotAuthProvider } from '../../services/copilotAuthService';
import { copilotLogger } from '../../utils/logger';

interface CopilotAssistantProps {
  containerId: string;
  matterTitle?: string;
}

/**
 * Copilot Assistant Component
 * 
 * This component integrates the SharePoint Embedded Copilot chat functionality
 * into the legal matter management application. It provides AI-powered document
 * analysis and legal assistance within the context of a specific matter's
 * SharePoint Embedded container.
 */
export const CopilotAssistant: React.FC<CopilotAssistantProps> = ({
  containerId,
  matterTitle = 'Legal Matter'
}) => {
  const [initError, setInitError] = useState<string | null>(null);
  const authTestRef = useRef<boolean>(false);
  
  // Memoize chat config to prevent unnecessary re-renders
  const chatConfig = useMemo(() => ({
    header: `Medical Records Review`,
    instruction: `You are a medical nurse paralegal, specializing in medical records review for matter "${matterTitle}". 
    Focus on providing accurate analysis of medical records, timelines, providers, and costs. 
    Always be precise, cite specific document sections when possible, and highlight 
    important medical and legal considerations.`,
    locale: "en",
    theme: "dark",
    colorScheme: "dark",
    suggestedPrompts: [
      "Summarize the medical records for this matter",
      "Create a historical timeline of medical services rendered", 
      "Extract the providers rendering services and the cost of their services"
    ],
    welcomeMessage: "How can I help?"
  }), [matterTitle]);

  // Test auth provider only once per container
  useEffect(() => {
    if (authTestRef.current) return;
    
    const testAuth = async () => {
      try {
        copilotLogger.debug('Testing Copilot auth provider...');
        const token = await copilotAuthProvider.getToken();
        copilotLogger.info('Auth provider test successful', { tokenLength: token.length });
        authTestRef.current = true;
      } catch (error) {
        copilotLogger.error('Auth provider test failed', error);
        setInitError(error instanceof Error ? error.message : 'Auth test failed');
      }
    };
    
    testAuth();
  }, [containerId]); // Only re-test when container changes

  // Handle chat API initialization - memoized to prevent re-renders
  const handleApiReady = useCallback((api: any) => {
    copilotLogger.debug('Chat API ready for container', containerId);
    
    // Auto-launch chat with legal-specific configuration
    const launchChat = async () => {
      try {
        copilotLogger.debug('Launching chat...');
        
        await api.openChat(chatConfig);
        copilotLogger.info('Chat launched successfully');
        
        // Try to inject dark theme styles into iframe after a delay
        setTimeout(() => {
          try {
            const iframe = document.querySelector('.copilot-chat-container iframe') as HTMLIFrameElement;
            if (iframe && iframe.contentDocument) {
              const style = iframe.contentDocument.createElement('style');
              style.textContent = `
                body { background-color: #0c101c !important; color: #fff9f4 !important; }
                [role="main"] { background-color: #fff9f4 !important; }
                .message[data-sender="assistant"] { background-color: #f0f1f5 !important; color: #392d2e !important; }
                input, textarea { background-color: #1b2336 !important; color: #fff9f4 !important; border-color: #2c3a63 !important; }
              `;
              iframe.contentDocument.head.appendChild(style);
              copilotLogger.debug('Dark theme styles injected');
            }
          } catch (error) {
            copilotLogger.debug('Could not inject styles (expected due to CORS)', error);
          }
        }, 2000);
        
      } catch (error) {
        copilotLogger.error('Failed to launch chat', error);
        setInitError(error instanceof Error ? error.message : 'Failed to launch chat');
      }
    };
    
    launchChat();
  }, [containerId, chatConfig]); // Use memoized chatConfig

  // Reset state when container changes
  useEffect(() => {
    setInitError(null);
    authTestRef.current = false; // Reset auth test for new container
  }, [containerId]);

  // Render error state first
  if (initError) {
    return (
      <div className="bg-gray-800 rounded-lg border border-red-600 p-6">
        <div className="text-center">
          <div className="text-red-400 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold text-white mb-2">Chat Initialization Failed</h3>
          <p className="text-red-300 mb-4">{initError}</p>
          
          <div className="text-left bg-gray-900 rounded-lg p-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-300 mb-2">Expected Issues:</h4>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>• Microsoft 365 Copilot licensing required</li>
              <li>• SharePoint admin tenant configuration needed</li>
              <li>• Container must be accessible and valid</li>
              <li>• Network connectivity to SharePoint services</li>
            </ul>
            <p className="text-xs text-gray-500 mt-3">
              This is normal during development before tenant configuration is complete.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Render the actual chat component
  copilotLogger.debug('Rendering ChatEmbedded component', { 
    hasAuthProvider: !!copilotAuthProvider, 
    containerId 
  });
  
  return (
    <div className="copilot-chat-wrapper h-full">
      <div 
        className="h-full bg-gray-800 rounded-lg border border-gray-700 overflow-hidden copilot-chat-container"
        style={{
          backgroundColor: '#0c101c',
          borderColor: '#2c3a63',
          '--colorLayerBackgroundApp': '#0c101c',
          '--Theme-Color-Background-100': '#0c101c',
          '--color-midnight-900': '#0c101c',
          '--color-saddle-150': '#fff9f4',
          '--color-saddle-750': '#392d2e',
          '--color-slate-150': '#f0f1f5',
        } as React.CSSProperties}
      >
        <ChatEmbedded
          authProvider={copilotAuthProvider}
          onApiReady={handleApiReady}
          containerId={containerId}
          style={{ 
            width: '100%', 
            height: '100%',
            border: 'none',
            borderRadius: '0.5rem',
            backgroundColor: '#0c101c',
            colorScheme: 'dark',
          } as React.CSSProperties}
        />
      </div>
    </div>
  );
};
